import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { reservasPublicasAPI } from '../services/api';
import './ReservaPublicaHorizontal.css';

const PHASE_PICK = 'pick';
const PHASE_FORM = 'form';
const PHASE_CONFIRM = 'confirm';

const TRANSLATIONS = {
  es: {
    loading: 'Cargando...',
    notAvailable: 'Reservas no disponibles',
    notAvailableDesc: 'El sistema de reservas no está habilitado en este momento. Contacta directamente con el profesional.',
    defaultTitle: 'Reserva de cita',
    pickDay: 'Elige un día',
    weekdays: ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'],
    locale: 'es-ES',
    prevMonth: 'Mes anterior',
    nextMonth: 'Mes siguiente',
    slotsFor: 'Horarios disponibles',
    loadingSlots: 'Cargando horarios...',
    noSlots: 'No hay horarios disponibles ese día.',
    selectDay: 'Selecciona un día en el calendario',
    contactInfo: 'Datos de contacto',
    at: 'a las',
    changeSelection: '← Cambiar fecha y hora',
    firstName: 'Nombre',
    lastName: 'Apellidos',
    email: 'Email',
    phone: 'Teléfono',
    modality: 'Modalidad',
    modalities: ['Presencial', 'Online'],
    welcome: 'Bienvenida',
    welcomePlaceholder: '¿Hay algo que quieras comentarnos antes de la cita?',
    submitting: 'Reservando...',
    confirm: 'Confirmar reserva',
    confirmed: 'Reserva confirmada',
    confirmedMsg: 'Tu cita ha quedado reservada.',
    bookAnother: 'Hacer otra reserva',
    formError: 'Completa nombre, apellidos y email',
    bookingError: 'Error al reservar',
  },
  en: {
    loading: 'Loading...',
    notAvailable: 'Bookings not available',
    notAvailableDesc: 'The booking system is not currently enabled. Please contact the professional directly.',
    defaultTitle: 'Book an appointment',
    pickDay: 'Choose a day',
    weekdays: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
    locale: 'en-US',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    slotsFor: 'Available times',
    loadingSlots: 'Loading times...',
    noSlots: 'No available times for this day.',
    selectDay: 'Select a day on the calendar',
    contactInfo: 'Contact details',
    at: 'at',
    changeSelection: '← Change date and time',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    phone: 'Phone',
    modality: 'Session type',
    modalities: ['In-person', 'Online'],
    welcome: 'Welcome message',
    welcomePlaceholder: 'Is there anything you\'d like to share before the appointment?',
    submitting: 'Booking...',
    confirm: 'Confirm booking',
    confirmed: 'Booking confirmed',
    confirmedMsg: 'Your appointment has been booked.',
    bookAnother: 'Book another',
    formError: 'Please fill in first name, last name and email',
    bookingError: 'Booking failed',
  },
};

function ReservaPublicaHorizontal() {
  const { username } = useParams();
  const [searchParams] = useSearchParams();
  const lang = (searchParams.get('lang') || 'es').toLowerCase();
  const t = useMemo(() => TRANSLATIONS[lang] || TRANSLATIONS.es, [lang]);

  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = 'transparent';
    document.documentElement.style.backgroundColor = 'transparent';
    const appEl = document.querySelector('.App');
    const prevApp = appEl ? appEl.style.backgroundColor : '';
    if (appEl) appEl.style.backgroundColor = 'transparent';
    return () => {
      document.body.style.backgroundColor = prev;
      document.documentElement.style.backgroundColor = '';
      if (appEl) appEl.style.backgroundColor = prevApp;
    };
  }, []);

  const [phase, setPhase] = useState(PHASE_PICK);
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notAvailable, setNotAvailable] = useState(false);

  const [month, setMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [form, setForm] = useState({
    nombre: '',
    apellidos: '',
    email: '',
    telefono: '',
    modalidad: 'Presencial',
    bienvenida: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await reservasPublicasAPI.getCoachInfo(username);
        if (!cancelled) setCoach(data);
      } catch (e) {
        if (!cancelled) {
          if (e.code === 'NOT_FOUND') setNotAvailable(true);
          else setError(e.message || 'Error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [username]);

  useEffect(() => {
    if (!selectedDate || !username) return;
    let cancelled = false;
    setLoadingSlots(true);
    setSelectedSlot(null);
    reservasPublicasAPI
      .getSlots(username, selectedDate)
      .then((data) => { if (!cancelled) setSlots(data.slots || []); })
      .catch(() => { if (!cancelled) setSlots([]); })
      .finally(() => { if (!cancelled) setLoadingSlots(false); });
    return () => { cancelled = true; };
  }, [username, selectedDate]);

  const handlePrevMonth = () => {
    setMonth((m) => {
      if (m.month === 0) return { year: m.year - 1, month: 11 };
      return { year: m.year, month: m.month - 1 };
    });
  };

  const handleNextMonth = () => {
    const today = new Date();
    const max = new Date(today.getFullYear() + (coach?.dias_anticipacion_max ?? 30), today.getMonth(), today.getDate());
    setMonth((m) => {
      const next = m.month === 11 ? { year: m.year + 1, month: 0 } : { year: m.year, month: m.month + 1 };
      const nextDate = new Date(next.year, next.month, 1);
      if (nextDate > max) return m;
      return next;
    });
  };

  const getDaysInMonth = () => {
    const first = new Date(month.year, month.month, 1);
    const last = new Date(month.year, month.month + 1, 0);
    const startPad = (first.getDay() + 6) % 7;
    const days = [];
    for (let i = 0; i < startPad; i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) days.push(d);
    return days;
  };

  const minDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + (coach?.dias_anticipacion_min ?? 1));
    return d.toISOString().slice(0, 10);
  };

  const maxDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + (coach?.dias_anticipacion_max ?? 30));
    return d.toISOString().slice(0, 10);
  };

  const isDatePast = (day) => {
    if (!day) return false;
    const d = new Date(month.year, month.month, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  };

  const isDateOutOfRange = (day) => {
    if (!day) return false;
    const dateStr = `${month.year}-${String(month.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return dateStr < minDate() || dateStr > maxDate();
  };

  const isDateUnavailable = (day) => {
    if (!day) return false;
    if (coach?.dias_disponibles && coach.dias_disponibles.length > 0) {
      const dow = new Date(month.year, month.month, day).getDay();
      if (!coach.dias_disponibles.includes(dow)) return true;
    }
    return false;
  };

  const isDateToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return month.year === today.getFullYear() && month.month === today.getMonth() && day === today.getDate();
  };

  const isDateSelectable = (day) => {
    if (!day) return false;
    return !isDatePast(day) && !isDateOutOfRange(day) && !isDateUnavailable(day);
  };

  const isDateSelected = (day) => {
    if (!day || !selectedDate) return false;
    const dateStr = `${month.year}-${String(month.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return dateStr === selectedDate;
  };

  const selectDate = (day) => {
    if (!isDateSelectable(day)) return;
    const dateStr = `${month.year}-${String(month.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
  };

  const selectSlot = (slot) => {
    setSelectedSlot(slot);
    setPhase(PHASE_FORM);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const submitBooking = async () => {
    const { nombre, apellidos, email, telefono, modalidad, bienvenida } = form;
    if (!nombre?.trim() || !apellidos?.trim() || !email?.trim() || !selectedDate || !selectedSlot) {
      setError(t.formError);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await reservasPublicasAPI.createBooking({
        usuario_username: username,
        nombre: nombre.trim(),
        apellidos: apellidos.trim(),
        email: email.trim(),
        telefono: (telefono || '').trim(),
        modalidad: modalidad || 'Presencial',
        bienvenida: (bienvenida || '').trim(),
        fecha_hora: `${selectedDate} ${selectedSlot}`,
      });
      setConfirmResult(result);
      setPhase(PHASE_CONFIRM);
    } catch (e) {
      setError(e.message || t.bookingError);
    } finally {
      setSubmitting(false);
    }
  };

  const resetFlow = () => {
    setPhase(PHASE_PICK);
    setSelectedDate(null);
    setSelectedSlot(null);
    setForm({ nombre: '', apellidos: '', email: '', telefono: '', modalidad: 'Presencial', bienvenida: '' });
    setConfirmResult(null);
    setError(null);
  };

  const formatSelectedDate = () => {
    if (!selectedDate) return '';
    const d = new Date(selectedDate + 'T12:00:00');
    return d.toLocaleDateString(t.locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return <div className="rbh-container"><div className="rbh-loading">{t.loading}</div></div>;
  }

  if (notAvailable) {
    return (
      <div className="rbh-container">
        <div className="rbh-not-available">
          <div className="rbh-not-available-icon">📅</div>
          <h2>{t.notAvailable}</h2>
          <p>{t.notAvailableDesc}</p>
        </div>
      </div>
    );
  }

  if (error && !coach) {
    return <div className="rbh-container"><div className="rbh-error-full">{error}</div></div>;
  }

  return (
    <div className="rbh-container">
      {phase === PHASE_PICK && (
        <div className="rbh-pick-layout">
          {/* Left: Calendar */}
          <div className="rbh-calendar-panel">
            <p className="rbh-welcome-title"><strong>{t.defaultTitle}</strong></p>
            {(() => {
              const msg = lang === 'en' ? (coach?.mensaje_bienvenida_en || coach?.mensaje_bienvenida) : coach?.mensaje_bienvenida;
              return msg ? <p className="rbh-welcome">{msg}</p> : null;
            })()}
            <div className="rbh-calendar">
              <div className="rbh-calendar-header">
                <button type="button" onClick={handlePrevMonth} aria-label={t.prevMonth}>‹</button>
                <span>{new Date(month.year, month.month).toLocaleDateString(t.locale, { month: 'long', year: 'numeric' })}</span>
                <button type="button" onClick={handleNextMonth} aria-label={t.nextMonth}>›</button>
              </div>
              <div className="rbh-weekdays">
                {t.weekdays.map((d) => <span key={d}>{d}</span>)}
              </div>
              <div className="rbh-days">
                {getDaysInMonth().map((day, i) => {
                  let cls = 'rbh-day';
                  if (!day) cls += ' empty';
                  else if (isDateSelectable(day)) cls += ' selectable';
                  else if (isDatePast(day)) cls += ' past';
                  else if (isDateUnavailable(day)) cls += ' unavailable';
                  else cls += ' out-of-range';
                  if (day && isDateToday(day)) cls += ' today';
                  if (day && isDateSelected(day)) cls += ' selected';
                  return (
                    <button
                      key={i}
                      type="button"
                      className={cls}
                      onClick={() => selectDate(day)}
                      disabled={!day || !isDateSelectable(day)}
                    >
                      {day || ''}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right: Time slots */}
          <div className="rbh-slots-panel">
            {!selectedDate ? (
              <div className="rbh-slots-placeholder">
                <span className="rbh-slots-placeholder-icon">🕐</span>
                <p>{t.selectDay}</p>
              </div>
            ) : (
              <>
                <h3 className="rbh-slots-title">{t.slotsFor}</h3>
                <p className="rbh-slots-date">{formatSelectedDate()}</p>
                {loadingSlots ? (
                  <div className="rbh-slots-loading">{t.loadingSlots}</div>
                ) : slots.length === 0 ? (
                  <p className="rbh-slots-empty">{t.noSlots}</p>
                ) : (
                  <div className="rbh-slots-grid">
                    {slots.map((slot) => (
                      <button
                        key={slot}
                        type="button"
                        className="rbh-slot-btn"
                        onClick={() => selectSlot(slot)}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {phase === PHASE_FORM && (
        <div className="rbh-form-layout">
          <button type="button" className="rbh-back" onClick={() => { setPhase(PHASE_PICK); setSelectedSlot(null); }}>
            {t.changeSelection}
          </button>
          <h2 className="rbh-form-title">{t.contactInfo}</h2>
          <p className="rbh-form-summary">
            {formatSelectedDate()} {t.at} {selectedSlot}
          </p>
          {error && <div className="rbh-form-error">{error}</div>}
          <form className="rbh-form" onSubmit={(e) => { e.preventDefault(); submitBooking(); }}>
            <div className="rbh-form-row">
              <label>
                {t.firstName} *
                <input name="nombre" value={form.nombre} onChange={handleFormChange} required />
              </label>
              <label>
                {t.lastName} *
                <input name="apellidos" value={form.apellidos} onChange={handleFormChange} required />
              </label>
            </div>
            <div className="rbh-form-row">
              <label>
                {t.email} *
                <input name="email" type="email" value={form.email} onChange={handleFormChange} required />
              </label>
              <label>
                {t.phone}
                <input name="telefono" type="tel" value={form.telefono} onChange={handleFormChange} />
              </label>
            </div>
            <div className="rbh-form-row">
              <label>
                {t.modality}
                <select name="modalidad" value={form.modalidad} onChange={handleFormChange}>
                  {t.modalities.map((m, i) => (
                    <option key={m} value={['Presencial', 'Online'][i]}>{m}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="rbh-form-row">
              <label>
                {t.welcome}
                <textarea
                  name="bienvenida"
                  value={form.bienvenida}
                  onChange={handleFormChange}
                  placeholder={t.welcomePlaceholder}
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </label>
            </div>
            <button type="submit" className="rbh-submit" disabled={submitting}>
              {submitting ? t.submitting : t.confirm}
            </button>
          </form>
        </div>
      )}

      {phase === PHASE_CONFIRM && confirmResult && (
        <div className="rbh-confirm-layout">
          <div className="rbh-confirm-icon">✅</div>
          <h2>{t.confirmed}</h2>
          <p>{confirmResult.mensaje ? (lang === 'en' ? t.confirmedMsg : confirmResult.mensaje) : t.confirmedMsg}</p>
          <p className="rbh-confirm-detail">
            {formatSelectedDate()} {t.at} {selectedSlot} ({confirmResult.duracion_minutos} min)
          </p>
          <button type="button" className="rbh-submit" onClick={resetFlow}>
            {t.bookAnother}
          </button>
        </div>
      )}
    </div>
  );
}

export default ReservaPublicaHorizontal;
